import React, { useState, useRef, useEffect, useMemo } from 'react';

/**
 * ProductCombobox — typeahead/autocomplete drop-in replacement for the
 * native <select> used to pick a finished good or raw material in Sales,
 * Enterprise, Production, Recipe forms etc.
 *
 * Usage:
 *   <ProductCombobox
 *     products={products}                  // [{id, name, item_type, unit, current_stock, product_code}]
 *     value={item.product_id}              // current selection id
 *     onChange={(productId, productObj) => setItem({...})}
 *     groupByType                          // optional — separate FG / RM groups
 *     placeholder="Type product…"
 *     testId={`item-product-${i}`}
 *     allowFreeText                        // optional — allow typing a custom name not in list
 *   />
 *
 * Behaviour:
 *  - Shows the current selection's name in the input by default
 *  - Typing filters by name (case-insensitive substring) AND by product_code
 *  - Arrow keys navigate, Enter picks, Esc closes
 *  - On mobile the dropdown scrolls inside the modal — uses max-h plus
 *    overflow-y-auto and stays beneath the input.
 */
export default function ProductCombobox({
  products = [],
  value = '',
  onChange,
  placeholder = 'Type to search…',
  groupByType = false,
  allowFreeText = false,
  filter = null,                  // optional (p) => bool
  testId,
  size = 'sm',                     // 'sm' | 'md'
  className = '',
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [highlight, setHighlight] = useState(0);
  const inputRef = useRef(null);
  const listRef = useRef(null);
  const containerRef = useRef(null);

  const filteredProducts = useMemo(() => {
    return filter ? products.filter(filter) : products;
  }, [products, filter]);

  const selected = useMemo(
    () => filteredProducts.find(p => p.id === value) || null,
    [filteredProducts, value],
  );

  // Sync the input text with the selected product when value changes externally.
  useEffect(() => {
    if (!open) {
      setQuery(selected ? selected.name : '');
    }
  }, [selected, open]);

  // Close on outside click
  useEffect(() => {
    const onClick = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
        setQuery(selected ? selected.name : '');
      }
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [selected]);

  const matches = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return filteredProducts.slice(0, 50);  // cap when empty
    return filteredProducts.filter(p => {
      const name = (p.name || '').toLowerCase();
      const code = (p.product_code || '').toLowerCase();
      return name.includes(q) || code.includes(q);
    }).slice(0, 50);
  }, [filteredProducts, query]);

  // Group by type when requested (FG first, then RM).
  const groups = useMemo(() => {
    if (!groupByType) return [{ label: '', items: matches }];
    const fg = matches.filter(p => p.item_type === 'finished');
    const rm = matches.filter(p => p.item_type === 'raw');
    const other = matches.filter(p => p.item_type !== 'finished' && p.item_type !== 'raw');
    const out = [];
    if (fg.length) out.push({ label: 'Finished Goods', items: fg });
    if (rm.length) out.push({ label: 'Raw Materials', items: rm });
    if (other.length) out.push({ label: 'Other', items: other });
    return out;
  }, [matches, groupByType]);

  // Flat list for keyboard nav (preserves group order).
  const flat = useMemo(() => groups.flatMap(g => g.items), [groups]);

  useEffect(() => {
    if (highlight >= flat.length) setHighlight(0);
  }, [flat, highlight]);

  const pick = (p) => {
    if (!p) return;
    onChange?.(p.id, p);
    setQuery(p.name);
    setOpen(false);
  };

  const onKey = (e) => {
    if (!open && (e.key === 'ArrowDown' || e.key === 'Enter')) {
      setOpen(true);
      return;
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlight(h => Math.min(h + 1, Math.max(0, flat.length - 1)));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlight(h => Math.max(0, h - 1));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (flat[highlight]) pick(flat[highlight]);
      else if (allowFreeText && query.trim()) {
        onChange?.('', { id: '', name: query.trim(), free_text: true });
        setOpen(false);
      }
    } else if (e.key === 'Escape') {
      setOpen(false);
      setQuery(selected ? selected.name : '');
      inputRef.current?.blur();
    }
  };

  // Make highlighted row scroll into view
  useEffect(() => {
    if (!open || !listRef.current) return;
    const el = listRef.current.querySelector(`[data-row-idx="${highlight}"]`);
    if (el) el.scrollIntoView({ block: 'nearest' });
  }, [highlight, open]);

  const sizeCls = size === 'md' ? 'px-3 py-2 text-sm' : 'px-2 py-1.5 text-xs';

  return (
    <div ref={containerRef} className={`relative ${className}`}>
      <input
        ref={inputRef}
        type="text"
        value={open ? query : (selected ? selected.name : query)}
        onChange={e => {
          setQuery(e.target.value);
          setOpen(true);
          setHighlight(0);
          if (!e.target.value) onChange?.('', null);
        }}
        onFocus={() => {
          setOpen(true);
          // On mobile, auto-clear so the keyboard appears with empty field
          // ready for typing — much faster than fighting with the existing text.
          if (selected) setQuery('');
        }}
        onKeyDown={onKey}
        placeholder={placeholder}
        autoComplete="off"
        data-testid={testId}
        className={`w-full bg-zinc-800 border border-zinc-700 rounded-lg text-zinc-100 focus:outline-none focus:border-amber-500 ${sizeCls}`}
      />
      {open && (
        <div
          ref={listRef}
          className="absolute z-50 left-0 right-0 mt-1 bg-zinc-900 border border-zinc-700 rounded-lg shadow-xl max-h-64 overflow-y-auto"
          data-testid={testId ? `${testId}-options` : undefined}
        >
          {flat.length === 0 && (
            <div className="px-3 py-2 text-zinc-500 text-xs">
              {allowFreeText && query.trim()
                ? <>No match — press <kbd className="px-1 bg-zinc-800 rounded">Enter</kbd> to use “{query}”</>
                : 'No products found'}
            </div>
          )}
          {groups.map((g, gi) => (
            <div key={gi}>
              {g.label && (
                <div className="px-3 py-1 text-[10px] font-semibold text-amber-500/80 uppercase tracking-wide bg-zinc-950 border-b border-zinc-800 sticky top-0">
                  {g.label}
                </div>
              )}
              {g.items.map(p => {
                const idx = flat.indexOf(p);
                const active = idx === highlight;
                return (
                  <button
                    key={p.id}
                    type="button"
                    data-row-idx={idx}
                    onMouseDown={(e) => { e.preventDefault(); pick(p); }}
                    onMouseEnter={() => setHighlight(idx)}
                    className={`w-full text-left px-3 py-2 text-xs flex items-center gap-2 border-b border-zinc-800/60 last:border-b-0 ${active ? 'bg-amber-500/15 text-amber-100' : 'text-zinc-200 hover:bg-zinc-800'}`}>
                    {p.product_code && (
                      <span className="font-mono text-[10px] text-amber-400 bg-amber-500/10 px-1.5 py-0.5 rounded border border-amber-500/30 flex-shrink-0">
                        {p.product_code}
                      </span>
                    )}
                    <span className="flex-1 truncate">{p.name}</span>
                    <span className="text-zinc-500 text-[10px] whitespace-nowrap">
                      stk {p.current_stock || 0}{p.unit ? ` ${p.unit}` : ''}
                    </span>
                  </button>
                );
              })}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
